import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchChatAnnouncements,
  pinChatAnnouncement,
  unpinChatAnnouncement,
} from '../../../../api/chatAnnouncements';

const normalizeMessageId = (value) => (value == null ? '' : String(value).trim());

const resolveRoomKey = ({ mode, scope, artistId, eventId, threadId, enabled }) => {
  if (!enabled) return '';
  if (scope === 'group') return threadId ? `group:${mode}:${threadId}` : '';
  if (scope === 'event') return eventId ? `event:${mode}:${eventId}` : '';
  if (mode === 'artist') return 'individual:artist:self';
  return artistId ? `individual:admin:${artistId}` : '';
};

const useChatAnnouncements = ({
  mode = 'admin',
  scope = 'individual',
  artistId = '',
  eventId = '',
  threadId = '',
  enabled = true,
  onError = null,
} = {}) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMessageId, setActionMessageId] = useState('');
  const [listOpen, setListOpen] = useState(false);

  const roomKey = useMemo(
    () => resolveRoomKey({ mode, scope, artistId, eventId, threadId, enabled }),
    [artistId, enabled, eventId, mode, scope, threadId]
  );

  const requestParams = useMemo(
    () => ({ mode, scope, artistId, eventId, threadId }),
    [artistId, eventId, mode, scope, threadId]
  );

  const reportError = useCallback(
    (error, fallbackMessage) => {
      if (error?.name === 'AbortError') return;
      if (typeof onError === 'function') {
        onError(error?.message || fallbackMessage);
      }
    },
    [onError]
  );

  const refreshAnnouncements = useCallback(
    async ({ signal, silent = false } = {}) => {
      if (!roomKey) {
        setAnnouncements([]);
        return [];
      }
      if (!silent) setLoading(true);
      try {
        const data = await fetchChatAnnouncements(requestParams, { signal });
        const next = Array.isArray(data?.announcements) ? data.announcements : [];
        setAnnouncements(next);
        return next;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [requestParams, roomKey]
  );

  useEffect(() => {
    if (!roomKey) {
      setAnnouncements([]);
      setListOpen(false);
      return undefined;
    }
    const controller = new AbortController();
    refreshAnnouncements({ signal: controller.signal }).catch((error) => {
      reportError(error, 'アナウンス一覧の取得に失敗しました。');
    });
    return () => controller.abort();
  }, [refreshAnnouncements, reportError, roomKey]);

  const applyResponseAnnouncements = useCallback(
    async (data) => {
      const next = Array.isArray(data?.announcements) ? data.announcements : null;
      if (next) {
        setAnnouncements(next);
        return next;
      }
      return refreshAnnouncements({ silent: true });
    },
    [refreshAnnouncements]
  );

  const pinAnnouncement = useCallback(
    async (messageId) => {
      const normalizedMessageId = normalizeMessageId(messageId);
      if (!roomKey || !normalizedMessageId) return [];
      setActionMessageId(normalizedMessageId);
      try {
        const data = await pinChatAnnouncement({ ...requestParams, messageId: normalizedMessageId });
        return await applyResponseAnnouncements(data);
      } catch (error) {
        reportError(error, 'アナウンス設定に失敗しました。');
        throw error;
      } finally {
        setActionMessageId('');
      }
    },
    [applyResponseAnnouncements, reportError, requestParams, roomKey]
  );

  const unpinAnnouncement = useCallback(
    async (messageId) => {
      const normalizedMessageId = normalizeMessageId(messageId);
      if (!roomKey || !normalizedMessageId) return [];
      setActionMessageId(normalizedMessageId);
      try {
        const data = await unpinChatAnnouncement({ ...requestParams, messageId: normalizedMessageId });
        return await applyResponseAnnouncements(data);
      } catch (error) {
        reportError(error, 'アナウンス解除に失敗しました。');
        throw error;
      } finally {
        setActionMessageId('');
      }
    },
    [applyResponseAnnouncements, reportError, requestParams, roomKey]
  );

  const announcementMessageIdSet = useMemo(
    () => new Set((Array.isArray(announcements) ? announcements : []).map((item) => normalizeMessageId(item?.messageId)).filter(Boolean)),
    [announcements]
  );

  const isAnnouncedMessage = useCallback(
    (messageId) => announcementMessageIdSet.has(normalizeMessageId(messageId)),
    [announcementMessageIdSet]
  );

  return {
    announcements,
    latestAnnouncement: announcements[0] || null,
    announcementCount: announcements.length,
    loading,
    actionMessageId,
    listOpen,
    setListOpen,
    refreshAnnouncements,
    pinAnnouncement,
    unpinAnnouncement,
    isAnnouncedMessage,
  };
};

export default useChatAnnouncements;
